import { useMemo, useState } from "react";
import { Link } from "wouter";
import { ArrowRight, Search } from "lucide-react";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";
import { FinalBand, PageHero } from "@/components/marketing/sections";
import { Input } from "@/components/ui/input";
import { useSEO } from "@/hooks/use-seo";
import { seoFor } from "@/data/marketing-routes";
import { ALL_FEATURE_GROUPS, CORE_PAGES, OUTCOME_CARDS } from "@/data/core-pages";
import { searchNormalize } from "@/lib/search-normalize";

/** All features (build F6, Appendix A7): the five core cards, then a searchable list. */
export default function FeaturesAllPage() {
  useSEO(seoFor("/features"));
  const [query, setQuery] = useState("");

  const coreGroup = useMemo(
    () => ({ name: "The core five", items: CORE_PAGES.map((p) => ({ name: p.name, href: p.path })) as Array<{ name: string; href?: string; badge?: string }> }),
    [],
  );

  const groups = useMemo(() => {
    const q = searchNormalize(query);
    const all = [coreGroup, ...ALL_FEATURE_GROUPS];
    if (!q) return all;
    return all
      .map((g) => ({
        ...g,
        items: searchNormalize(g.name).includes(q) ? g.items : g.items.filter((i) => searchNormalize(i.name).includes(q)),
      }))
      .filter((g) => g.items.length > 0);
  }, [query, coreGroup]);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <PublicHeader />
      <PageHero
        title="Everything in PadPulse"
        sub="Every plan includes all of it. Start with the five that matter most."
        ctaLocation="features_all"
      />

      <main className="flex-grow">
        <section className="bg-gray-50 px-4 py-14 md:py-20">
          <div className="mx-auto grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="list-core-cards">
            {OUTCOME_CARDS.slice(0, 5).map((card) => (
              <Link
                key={card.href}
                href={card.href}
                className="group flex flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
                data-testid={`card-core-${String(card.href ?? "").split("/").pop()}`}
              >
                <h2 className="text-lg font-semibold text-gray-900 group-hover:text-brand">{card.title}</h2>
                <p className="mt-2 flex-grow text-gray-700">{card.body}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand">
                  Learn more <ArrowRight size={14} aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </section>

        <section className="px-4 py-14 md:py-20">
          <div className="mx-auto max-w-4xl">
            <label htmlFor="feature-search" className="sr-only">
              Search features
            </label>
            <div className="relative">
              <Search size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
              <Input
                id="feature-search"
                type="search"
                placeholder="Search features"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-12 bg-white pl-10 text-gray-900"
                data-testid="input-feature-search"
              />
            </div>

            {groups.length === 0 ? (
              <p className="mt-10 text-center text-gray-600" data-testid="text-feature-search-empty">
                No features match “{query}”.
              </p>
            ) : (
              <div className="mt-10 grid gap-10 sm:grid-cols-2" data-testid="list-feature-groups">
                {groups.map((group) => (
                  <div key={group.name}>
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500">{group.name}</h2>
                    <ul className="mt-3 space-y-2">
                      {group.items.map((item) => (
                        <li key={item.name} className="text-gray-900">
                          {item.href ? (
                            <Link href={item.href} className="hover:text-brand hover:underline">
                              {item.name}
                            </Link>
                          ) : (
                            item.name
                          )}
                          {item.badge && (
                            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                              {item.badge}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <FinalBand location="features_all" />
      <PublicFooter />
    </div>
  );
}
